import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/feature/PageMeta';
import { featuredAnime, hotAnime, nbaVideos, latestNews } from '@/mocks/homeData';
import type { AnimeItem, NbaVideo } from '@/mocks/homeData';
import { useState } from 'react';

const allAnime: AnimeItem[] = [...featuredAnime, ...hotAnime];
const allNews = latestNews;

function getItem(id: string): { type: 'anime'; data: AnimeItem } | { type: 'nba'; data: NbaVideo } | { type: 'news'; data: typeof allNews[number] } | null {
  const anime = allAnime.find((a) => a.id === id);
  if (anime) return { type: 'anime', data: anime };

  const nba = nbaVideos.find((v) => v.id === id);
  if (nba) return { type: 'nba', data: nba };

  const news = allNews.find((n) => n.id === id);
  if (news) return { type: 'news', data: news };

  return null;
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getItem(id) : null;
  const [selectedEpisode, setSelectedEpisode] = useState<number | null>(null);

  if (!item) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16 text-center">
          <h1 className="text-2xl font-bold text-foreground-900 mb-4">内容未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600">返回首页</Link>
        </main>
        <Footer />
      </div>
    );
  }

  // SEO
  const seoTitle = item.type === 'anime'
    ? `${item.data.title} - 在线观看 | 51动漫`
    : item.type === 'nba'
      ? `${item.data.title} | 51动漫NBA赛事`
      : `${item.data.title} | 51动漫资讯`;

  const seoDesc = item.type === 'anime'
    ? `${item.data.title}，${item.data.description} 51动漫提供高清在线播放，永久免费。`
    : item.type === 'nba'
      ? `${item.data.title}，精彩NBA赛事视频回放。51动漫提供永久免费NBA赛事在线观看。`
      : item.data.summary;

  const getBreadcrumbCategory = (): { name: string; slug: string } => {
    if (item.type === 'nba') return { name: 'NBA赛事', slug: 'nba' };
    if (item.type === 'news') return { name: '最新资讯', slug: 'reboot' };
    const c = item.data.category;
    if (c === '热血' || c === '冒险') return { name: '热血动漫', slug: 'reboot' };
    if (c === '恋爱') return { name: '恋爱动漫', slug: 'love' };
    if (c === '搞笑' || c === '日常') return { name: '搞笑动漫', slug: 'funny' };
    return { name: '热血动漫', slug: 'reboot' };
  };

  const breadcrumbCat = getBreadcrumbCategory();

  // Related items
  const relatedItems = allAnime.filter((a) => a.id !== id).slice(0, 5);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": item.type === 'anime' ? item.data.title : item.type === 'nba' ? item.data.title : item.data.title,
    "description": item.type === 'anime' ? item.data.description : item.type === 'nba' ? item.data.title : item.data.summary,
    "datePublished": "2026-07-07",
    "dateModified": "2026-07-07",
    "author": {
      "@type": "Organization",
      "name": "51动漫"
    },
    "publisher": {
      "@type": "Organization",
      "name": "51动漫",
      "url": "https://www.51dm.com/"
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.51dm.com/" },
      { "@type": "ListItem", "position": 2, "name": breadcrumbCat.name, "item": `https://www.51dm.com/category/${breadcrumbCat.slug}` },
      { "@type": "ListItem", "position": 3, "name": item.type === 'anime' ? item.data.title : item.type === 'nba' ? item.data.title : item.data.title }
    ]
  };

  return (
    <>
      <PageMeta
        title={seoTitle}
        description={seoDesc}
        keywords={`51动漫, ${item.type === 'anime' ? item.data.title + ', ' + item.data.tags.join(', ') + ', 在线观看' : item.type === 'nba' ? 'NBA, ' + item.data.title + ', 赛事回放' : '动漫资讯, ' + item.data.title}`}
        canonicalUrl={`https://www.51dm.com/article/${id}`}
        ogType="article"
        schemaData={[articleSchema, breadcrumbSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          {/* Breadcrumb */}
          <div className="pt-20 bg-background-50">
            <div className="max-w-7xl mx-auto px-4 md:px-8 py-4">
              <nav aria-label="面包屑导航" className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap">
                <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
                <i className="ri-arrow-right-s-line"></i>
                <Link to={`/category/${breadcrumbCat.slug}`} className="hover:text-primary-500 transition-colors whitespace-nowrap">{breadcrumbCat.name}</Link>
                <i className="ri-arrow-right-s-line"></i>
                <span className="text-foreground-700 font-medium line-clamp-1">{item.type === 'anime' ? item.data.title : item.type === 'nba' ? item.data.title : item.data.title}</span>
              </nav>
            </div>
          </div>

          {/* Main content area */}
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 md:py-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Left - Main content */}
              <div className="flex-1">
                {/* Video player placeholder */}
                <div className="aspect-video rounded-xl overflow-hidden bg-background-200 relative mb-6">
                  {item.type === 'anime' && (
                    <>
                      <img
                        src={item.data.cover}
                        alt={item.data.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/80 cursor-pointer hover:bg-primary-500 transition-colors duration-200">
                            <i className="ri-play-fill text-3xl md:text-4xl text-white ml-1"></i>
                          </div>
                          <p className="text-white text-sm">
                            {selectedEpisode !== null
                              ? `正在播放: ${item.data.episodes[selectedEpisode]}`
                              : '点击播放最新一集'}
                          </p>
                        </div>
                      </div>
                      {/* Player overlay info */}
                      <div className="absolute top-3 left-3 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur-sm text-white text-xs">
                        高清1080P
                      </div>
                      <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur-sm text-white text-xs">
                        {item.data.episode}
                      </div>
                    </>
                  )}
                  {item.type === 'nba' && (
                    <>
                      <img
                        src={item.data.cover}
                        alt={item.data.title}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="text-center">
                          <div className="w-16 h-16 md:w-20 md:h-20 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-500/80 cursor-pointer hover:bg-primary-500 transition-colors duration-200">
                            <i className="ri-play-fill text-3xl md:text-4xl text-white ml-1"></i>
                          </div>
                          <p className="text-white text-sm">点击播放赛事回放</p>
                        </div>
                      </div>
                      <div className="absolute top-3 left-3 px-3 py-1.5 rounded-lg bg-accent-500/80 text-white text-xs font-medium">
                        {item.data.teams}
                      </div>
                      <div className="absolute bottom-3 right-3 px-3 py-1.5 rounded-lg bg-black/50 backdrop-blur-sm text-white text-xs">
                        {item.data.duration}
                      </div>
                    </>
                  )}
                  {item.type === 'news' && (
                    <img
                      src={item.data.cover}
                      alt={item.data.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Title and meta */}
                <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                  {item.type === 'anime' ? item.data.title : item.type === 'nba' ? item.data.title : item.data.title}
                </h1>

                <div className="flex flex-wrap items-center gap-3 md:gap-5 text-xs md:text-sm text-foreground-500 mb-4">
                  {item.type === 'anime' && (
                    <>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-star-fill text-accent-500"></i>
                        <strong className="text-foreground-900 font-semibold">{item.data.rating}</strong> 分
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-eye-line"></i>
                        {item.data.views} 播放
                      </span>
                      <span>{item.data.episode}</span>
                      <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">{item.data.status}</span>
                    </>
                  )}
                  {item.type === 'nba' && (
                    <>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-eye-line"></i>
                        {item.data.views} 播放
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-time-line"></i>
                        {item.data.duration}
                      </span>
                      <span>{item.data.date}</span>
                    </>
                  )}
                  {item.type === 'news' && (
                    <>
                      <span className="px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">{item.data.category}</span>
                      <span>{item.data.date}</span>
                    </>
                  )}
                </div>

                {/* Tags */}
                {item.type === 'anime' && item.data.tags && (
                  <div className="flex flex-wrap items-center gap-2 mb-4">
                    {item.data.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 text-xs rounded-full bg-background-100 text-foreground-600 border border-background-200/60">
                        {tag}
                      </span>
                    ))}
                  </div>
                )}

                {/* Description */}
                {item.type === 'anime' && (
                  <div className="mb-6 p-4 rounded-xl bg-background-100 border border-background-200/50">
                    <h3 className="text-sm font-semibold text-foreground-900 mb-2">剧情简介</h3>
                    <p className="text-sm text-foreground-600 leading-relaxed">{item.data.description}</p>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 pt-4 border-t border-background-200/50">
                      <div>
                        <span className="text-xs text-foreground-400">年份</span>
                        <p className="text-sm font-medium text-foreground-800">{item.data.year}</p>
                      </div>
                      <div>
                        <span className="text-xs text-foreground-400">地区</span>
                        <p className="text-sm font-medium text-foreground-800">{item.data.region}</p>
                      </div>
                      <div>
                        <span className="text-xs text-foreground-400">制作公司</span>
                        <p className="text-sm font-medium text-foreground-800">{item.data.studio}</p>
                      </div>
                      <div>
                        <span className="text-xs text-foreground-400">状态</span>
                        <p className="text-sm font-medium text-foreground-800">{item.data.status}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Episodes */}
                {item.type === 'anime' && (
                  <div className="mb-6">
                    <h3 className="text-base font-semibold text-foreground-900 mb-3">选集</h3>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                      {item.data.episodes.map((ep, idx) => (
                        <button
                          key={idx}
                          onClick={() => setSelectedEpisode(idx)}
                          className={`px-3 py-2 text-xs rounded-lg text-center transition-all duration-200 cursor-pointer ${
                            selectedEpisode === idx
                              ? 'bg-primary-500 text-white'
                              : 'bg-background-100 text-foreground-700 border border-background-200/60 hover:border-primary-300 hover:text-primary-500'
                          } ${ep.includes('待更新') ? 'opacity-50 cursor-not-allowed' : ''}`}
                          disabled={ep.includes('待更新')}
                        >
                          {ep}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* News content */}
                {item.type === 'news' && (
                  <div className="mb-6 p-5 rounded-xl bg-background-100 border border-background-200/50">
                    <p className="text-sm text-foreground-600 leading-relaxed whitespace-pre-line">{item.data.summary}</p>
                    <p className="text-sm text-foreground-600 leading-relaxed mt-4">
                      本文由51动漫编辑整理发布。51动漫官网提供最新动漫下载与在线播放资源，同步更新NBA赛事视频。从热门新番到经典老片，这里是动漫迷和体育迷的随身追更站。欢迎收藏51动漫官网，永久免费看动漫就上51动漫。
                    </p>
                  </div>
                )}
              </div>

              {/* Right - Sidebar */}
              <div className="lg:w-80 flex-shrink-0">
                {/* Related/recommend sidebar */}
                <div className="sticky top-[80px]">
                  <h3 className="text-base font-semibold text-foreground-900 mb-4">相关推荐</h3>
                  <div className="space-y-3">
                    {relatedItems.map((anime) => (
                      <Link
                        key={anime.id}
                        to={`/article/${anime.id}`}
                        className="flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer group"
                      >
                        <div className="w-16 h-20 rounded-lg overflow-hidden bg-background-200 flex-shrink-0">
                          <img src={anime.cover} alt={anime.title} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2 mb-1">
                            {anime.title}
                          </h4>
                          <div className="flex items-center gap-2 text-xs text-foreground-400">
                            <span className="text-accent-500 font-medium">{anime.rating}</span>
                            <span>{anime.episode}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}